import { useState, useCallback } from "react";
import type { ConfirmVariant } from "@/components/common/ConfirmModal";

interface ModalState {
  open: boolean;
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  withNote?: boolean;
  notePlaceholder?: string;
  noteRequired?: boolean;
}

interface OpenOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  withNote?: boolean;
  notePlaceholder?: string;
  noteRequired?: boolean;
  onConfirm: (note?: string) => void;
}

const INITIAL: ModalState = { open: false, title: "" };

export function useConfirmModal() {
  const [state, setState] = useState<ModalState>(INITIAL);
  const [onConfirmRef, setOnConfirmRef] = useState<{
    fn: (note?: string) => void;
  }>({ fn: () => {} });

  const confirm = useCallback((opts: OpenOptions) => {
    const { onConfirm, ...rest } = opts;
    setState({ open: true, ...rest });
    setOnConfirmRef({ fn: onConfirm });
  }, []);

  const handleConfirm = useCallback(
    (note?: string) => {
      onConfirmRef.fn(note);
      setState(INITIAL);
    },
    [onConfirmRef],
  );

  const handleCancel = useCallback(() => {
    setState(INITIAL);
  }, []);

  return {
    modalProps: {
      ...state,
      onConfirm: handleConfirm,
      onCancel: handleCancel,
    },
    confirm,
  };
}
